// src/scenes/GameScene.js
// v7 - Inherits from BaseGameScene, handles SP game over, uses notifications.

import BaseGameScene from "./BaseGameScene.js";
import { saveScoreToFirestore } from "../services/authService.js";
import { auth, db, firebase } from "../firebaseConfig.js"; // Import firebase for Timestamp
import Phaser from 'phaser';
import sceneManager from "../utils/SceneManager.js"; // For transitioning
import { hasSpecialAccess, getSpecialAccessUserId, getSpecialAccessDisplayName } from '../services/specialAccessService.js'; // For guest naming

class GameScene extends BaseGameScene {
  constructor() {
    super({ key: "GameScene" }); // Use the correct scene key

    // Initialize scene-specific properties
    this.instructionsText = null; // Specific UI for this scene
    this.padding = 20; // Define padding if used directly here
  }

  // --- Scene Lifecycle Methods ---

  init(data) {
    super.init(); // Call BaseGameScene init
    console.log(`[${this.scene.key}] Initializing...`);
    this.instructionsText = null; // Reset specific UI
  }

  create() {
    console.log(`[${this.scene.key}] Create START`);
    const topRibbon = document.getElementById("top-ribbon"); if (topRibbon) topRibbon.style.display = 'flex';
    const gameContainer = document.getElementById("game-container"); if (gameContainer) { gameContainer.style.top = "50px"; gameContainer.style.height = "calc(100% - 50px)"; }

    try {
        super.create(); // Calls BaseGameScene create
        console.log(`[${this.scene.key}] BaseGameScene create() completed.`);
        this.createSinglePlayerUI(); // Add SP specific UI
        console.log(`[${this.scene.key}] Create FINISHED.`);
    } catch (error) {
        console.error(`[${this.scene.key}] CRITICAL ERROR during create:`, error);
        this.showMessage("Game failed to load. Returning to menu.", 5000, true);
        this.time.delayedCall(5000, () => sceneManager.switchToScene("MainMenuScene"));
    }
  }

  createSinglePlayerUI() {
    console.log(`[${this.scene.key}] Creating single player UI`);
    if (this.instructionsText) { try { this.instructionsText.destroy(); } catch(e){} this.instructionsText = null; }

    this.time.delayedCall(1000, () => {
        if (!this.scene?.systems?.scenePlugin?.isActive(this.scene.key)) return;
        const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
        const controlsMessage = isMobile ? "Tap blue corners to move" : "Use Alt+H+G+A+(L/R/T/B) for shortcuts";
        this.showNotification(controlsMessage, "#00FFFF", 5000); // Cyan color, 5 seconds
        if (this.dashboard?.addEvent) { this.dashboard.addEvent("Welcome to Slide Wars!", "#FFFFFF"); this.dashboard.addEvent(controlsMessage, "#00FFFF"); }
    });
  }

  // --- Game Over Handling ---

  async onGameOver(reason = "unknown") {
    if (this.gameOverProcessed) { console.log(`[${this.scene.key}] onGameOver already processed.`); return; }
    this.gameOverProcessed = true; this.gameOver = true;
    console.log(`[${this.scene.key}] onGameOver START. Reason: ${reason}, Final Score: ${this.score}`);
  
    if (this.physics && !this.physics.world.isPaused) this.physics.pause();
  
    let finalPlayerName = 'Guest';
    let finalUserId = 'guest_' + Date.now();
    const user = auth.currentUser;
    const specialAccess = hasSpecialAccess();
    
    if (user) {
      finalUserId = user.uid;
      try {
        // First check user's profile in Firestore
        const profileDoc = await db.collection("userProfiles").doc(user.uid).get();
        if (profileDoc.exists && profileDoc.data().displayName) {
          finalPlayerName = profileDoc.data().displayName;
          console.log(`[${this.scene.key}] Using profile display name: ${finalPlayerName}`);
        } else if (user.displayName) {
          // Fall back to Firebase Auth display name
          finalPlayerName = user.displayName;
          console.log(`[${this.scene.key}] Using auth display name: ${finalPlayerName}`);
        } else if (user.email) {
          // Fall back to email if no display name
          finalPlayerName = user.email;
          console.log(`[${this.scene.key}] Using email as name: ${finalPlayerName}`);
        } else {
          finalPlayerName = "Player";
          console.log(`[${this.scene.key}] No user identifiers found, using default name`);
        }
      } catch (e) {
        // Handle errors when fetching profile
        console.error(`[${this.scene.key}] Error fetching user profile:`, e);
        finalPlayerName = user.displayName || user.email || "Player";
      }
    } else if (specialAccess) {
      // Guest+ user
      finalUserId = getSpecialAccessUserId();
      finalPlayerName = getSpecialAccessDisplayName();
    } else {
      // Regular guest
      finalPlayerName = localStorage.getItem("tempSimpsonsName") || `Guest-${Math.floor(Math.random() * 1000)}`;
      if (!localStorage.getItem("tempSimpsonsName")) {
        localStorage.setItem("tempSimpsonsName", finalPlayerName);
      }
    }
    
    console.log(`[${this.scene.key}] Determined player identity - ID: ${finalUserId}, Name: ${finalPlayerName}`);
  
    // ... rest of the method remains the same ...
    try {
        let playerRank = null; let totalPlayers = 0; const gameTypeForRanking = "Shortcut";
        try {
            if (db && typeof db.collection === 'function') {
                const snapshot = await db.collection("scores").where("game", "==", gameTypeForRanking).orderBy("score", "desc").limit(100).get();
                const scores = []; snapshot.forEach(doc => scores.push({ score: doc.data().score, uid: doc.data().userUID }));
                totalPlayers = scores.length; let rank = 1; for (const s of scores) { if (this.score >= s.score) break; rank++; }
                if (user && scores.some(s => s.score === this.score)) { const sameScoreRank = scores.findIndex(s => s.score < this.score); const rankAmongEquals = scores.slice(0, sameScoreRank === -1 ? scores.length : sameScoreRank).filter(s => s.score === this.score).findIndex(s => s.uid === user.uid); if(rankAmongEquals !== -1) rank = scores.findIndex(s => s.score === this.score) + 1 + rankAmongEquals; }
                playerRank = rank > scores.length ? null : rank;
                console.log(`[${this.scene.key}] Rank calculated - Rank: ${playerRank}, Total Players (fetched): ${totalPlayers}`);
            }
        } catch (e) { console.error(`[${this.scene.key}] Error fetching global rank data:`, e); }

        const gameTypeForSave = "Shortcut";
        const gameOverData = { score: this.score, gameType: gameTypeForSave, playerScores: [{ userId: finalUserId, player: finalPlayerName, name: finalPlayerName, score: this.score, rank: 1 }], playerRank: playerRank, totalPlayers: totalPlayers };

        console.log(`[${this.scene.key}] Attempting to save score (async)...`);
        saveScoreToFirestore(gameOverData, true)
            .then((saved) => console.log(`[${this.scene.key}] Score save initiated: ${saved}`))
            .catch(err => console.error(`[${this.scene.key}] Error during score save attempt:`, err));

        console.log(`[${this.scene.key}] Preparing transition to MultiplayerGameOverScene with data:`, JSON.stringify(gameOverData));

        this.time.delayedCall(150, () => {
            if (this.scene?.systems?.scenePlugin?.isActive(this.scene.key)) {
                try { console.log(`[${this.scene.key}] Calling sceneManager.switchToScene...`); sceneManager.switchToScene('MultiplayerGameOverScene', gameOverData); }
                catch (e) { console.error(`[${this.scene.key}] Error starting MultiplayerGameOverScene:`, e); try { sceneManager.switchToScene('MainMenuScene'); } catch (e2) { console.error(`[${this.scene.key}] Fallback to MainMenuScene failed:`, e2);} }
            } else { console.warn(`[${this.scene.key}] Scene became inactive before transition could start.`); }
        });

    } catch (error) {
        console.error(`[${this.scene.key}] Error in onGameOver logic:`, error);
        this.time.delayedCall(100, () => {
            const fallbackData = { score: this.score, gameType: "Shortcut", playerScores: [{ userId: finalUserId, player: finalPlayerName, name: finalPlayerName, score: this.score, rank: 1 }], playerRank: null, totalPlayers: 0 };
            try { sceneManager.switchToScene('MultiplayerGameOverScene', fallbackData); }
            catch (e) { console.error(`[${this.scene.key}] Fallback transition to GameOver failed:`, e); try { sceneManager.switchToScene('MainMenuScene'); } catch (e2) { console.error(`[${this.scene.key}] Critical fallback to MainMenu failed:`, e2); } }
        });
    }
  }

  hasCustomGameOver() { return true; }

  // --- Overridden Event Handlers (Optional) ---

  onBrickBreak() {
    super.onBrickBreak();
    if (this.dashboard?.addEvent) this.dashboard.addEvent("Brick destroyed! +10 pts", "#FFAA00");
  }
// Enhanced onTrailHit method for GameScene.js

onTrailHit(pointsEarned, isSpecialTrigger, isHyperspaceTrailHit = false) {
  // Call the parent method first
  super.onTrailHit(pointsEarned, isSpecialTrigger, isHyperspaceTrailHit);
  
  // Make sure score display is updated
  if (this.scoreText) {
    this.scoreText.setText(`Score: ${this.score.toLocaleString()}`);
  }
  
  // Add event to dashboard based on type of hit
  if (this.dashboard?.addEvent) {
    if (isSpecialTrigger) {
      this.dashboard.addEvent("Hyperspace triggered!", "#FF00FF");
    } else if (isHyperspaceTrailHit) {
      this.dashboard.addEvent(`Hyperspace trail hit: +${pointsEarned.toLocaleString()} pts`, "#FFD700");
    } else if (pointsEarned >= SCORE_GREEN_ASTEROID_TRAIL) {
      this.dashboard.addEvent(`Green asteroid hit: +${pointsEarned.toLocaleString()} pts`, "#00FF00");
    } else {
      this.dashboard.addEvent(`Trail hit: +${pointsEarned.toLocaleString()} pts`, "#FFAA00");
    }
  }
  
  // Log the event for debugging
  console.log(`[${this.scene.key}] Trail hit: ${pointsEarned} points, Special: ${isSpecialTrigger}, Hyperspace: ${isHyperspaceTrailHit}`);
}

  onPlayerHit() {
    super.onPlayerHit();
    if (this.livesText) this.livesText.setText(`Lives: ${this.lives}`);
  }

  // --- Scene Specific Methods ---

  handleResize(gameSize) {
    super.handleResize(gameSize);
    if (this.instructionsText?.active) {
        const width = this.cameras.main.width;
        this.instructionsText.setPosition(width / 2, this.padding);
        this.instructionsText.setWordWrapWidth(width * 0.9);
    }
  }

  shutdown() {
    console.log(`[${this.scene.key}] Shutting down...`);
    if (this.instructionsText) { try { this.instructionsText.destroy(); } catch(e){} this.instructionsText = null; }
    super.shutdown(); // Calls BaseGameScene's shutdown
    console.log(`[${this.scene.key}] Shutdown complete.`);
  }

} // End of GameScene class

export default GameScene;